import { localeMap } from 'app/modules/Constants';
import classnames from 'classnames';
import { addDays, compareAsc, format, isAfter, startOfDay } from 'date-fns';
import React from 'react';
import { connect } from 'react-redux';
import { openChildInput, openInput } from '../../../actions/baseActions';
import { selectFromDate, selectToDate, selectToTime } from '../../../actions/carActions';
import Helper from '../../modules/Helper';
import DatePicker from '../Common/DatePicker';
import FadeTransition from '../Common/FadeTransition';
import TimePicker from '../Common/TimePicker';

type DatePickerToProps = {
	selectedToDate: Date;
	selectedFromDate: Date;
	selectedFromTime: string;
	selectedToTime: string;
	name: string;
	openedInput?: string;
	openedChildInput?: string;
	autoJump?: boolean;
	preventSetInputBoxClassName?: boolean;
	lng: string;
	openInput: (name?: string) => void;
	openChildInput: (name?: string) => void;
	selectToTime: (time: string) => void;
	selectToDate: (date: Date) => void;
	selectFromDate: (date: Date) => void;
};
type DatePickerToState = {
	datepicker: boolean;
	timepicker: boolean;
	in: boolean;
};

class DatePickerTo extends React.Component<DatePickerToProps, DatePickerToState> {
	constructor(props: DatePickerToProps) {
		super(props);
		this.state = {
			datepicker: false,
			timepicker: false,
			in: false,
		};
	}

	onSelectDate = (to: Date, keyboardNavigation = false) => {
		let from = this.props.selectedFromDate;

		if (from === null) {
			from = new Date();
		}

		if (to === null) {
			to = addDays(from, 1);
		}

		if (isAfter(from, to)) {
			from = to;
		}

		this.props.selectFromDate(from);
		this.props.selectToDate(to);

		if (!keyboardNavigation) {
			this.openTimePickerBase(false);
		}

		if (window.innerWidth < 700) {
			Helper.doScrolling('#filter-form', 500);
		}
	};

	openDatePicker = () => {
		this.openDatePickerBase(!this.state.datepicker);
	};

	openDatePickerBase(open: boolean) {
		this.setState(
			{
				timepicker: false,
				datepicker: open,
				in: open,
			},
			() => {
				if (this.state.in) {
					this.props.openInput(this.props.name);
					this.props.openChildInput('to-date-picker');
				} else {
					this.props.openInput();
					this.props.openChildInput();
				}
			}
		);
	}

	openTimePicker = () => {
		this.openTimePickerBase(!this.state.timepicker);
	};
	openTimePickerBase = (open: boolean) => {
		this.setState(
			{
				datepicker: false,
				timepicker: open,
				in: open,
			},
			() => {
				if (this.state.in) {
					this.props.openInput(this.props.name);
					this.props.openChildInput('to-time-picker');
				} else {
					this.props.openInput();
					this.props.openChildInput();
				}
			}
		);
	};

	onSelectTime = (time: string) => {
		const { selectToTime, openInput, openChildInput } = this.props;

		this.setState(
			{
				timepicker: false,
				in: false,
			},
			() => {
				selectToTime(time);
				openInput();
				openChildInput();
			}
		);
	};

	componentDidUpdate(prevProps: DatePickerToProps) {
		const { openedInput } = this.props;

		if (!openedInput && openedInput !== prevProps.openedInput) {
			this.setState({
				datepicker: false,
				timepicker: false,
				in: false,
			});
		} else if (
			this.props.openedInput === prevProps.name &&
			this.props.openedInput !== prevProps.openedInput &&
			this.props.openedChildInput !== 'to-time-picker'
		) {
			this.openDatePickerBase(true);
		}
	}

	render() {
		const { lng, openedInput, openedChildInput, name, preventSetInputBoxClassName, selectedFromDate, selectedToDate } =
			this.props;
		const inputOpened = openedInput === name;
		const openedDatePicker = inputOpened && this.state.datepicker && openedChildInput === 'to-date-picker';
		const openedTimePicker = inputOpened && this.state.timepicker && openedChildInput === 'to-time-picker';
		const areDatesEqual = compareAsc(startOfDay(selectedFromDate), startOfDay(selectedToDate)) === 0;

		const dateDisplayFormat = lng === 'jp' ? 'M月 d日 (EEE)' : 'EEE, MMM d';

		return (
			<div
				id={name}
				className={classnames('flex-1', {
					'open-input': inputOpened,
					'input-box': !preventSetInputBoxClassName,
				})}
			>
				<div
					className={classnames('date-picker-parent input-control justify-content-between filled', {
						focused: inputOpened,
					})}
					aria-label={format(this.props.selectedToDate, dateDisplayFormat, { locale: localeMap[lng] })}
				>
					<label htmlFor={`${name}-input`} className="hidden-input-label">
						<input type="text" className="hidden-input" id={`${name}-input`} />
					</label>
					<div
						className={classnames('date-picker d-flex align-items-center flex-2 h-100', {
							'focused-child': openedDatePicker,
						})}
						onClick={this.openDatePicker}
					>
						<i className="icon-date-arr" />
						<div className="selected">
							{format(this.props.selectedToDate, dateDisplayFormat, { locale: localeMap[lng] })}
						</div>
					</div>
					<div
						className={classnames('d-flex align-items-center flex-1 h-100', {
							'focused-child': openedTimePicker,
						})}
						onClick={this.openTimePicker}
					>
						<div className="timepicker-show">
							<div className="selected-time">
								{this.props.selectedToTime === '12:00' ? Helper.trans('keywords.noon') : this.props.selectedToTime}
							</div>
							<div
								className={classnames('icon')}
								style={{
									transition: 'all .2s ease',
									transform: inputOpened && this.state.timepicker ? 'rotate(180deg)' : 'none',
								}}
							>
								<i className="icon-arrow opened" />
							</div>
						</div>
					</div>
				</div>
				<div style={{ position: 'relative' }}>
					<FadeTransition
						timeout={100}
						in={inputOpened && this.state.datepicker && this.props.openedChildInput === 'to-date-picker'}
					>
						<DatePicker
							before={this.props.selectedFromDate}
							selected={this.props.selectedToDate}
							onSelectDate={this.onSelectDate}
						/>
					</FadeTransition>
					<FadeTransition
						timeout={500}
						in={inputOpened && this.state.timepicker && this.props.openedChildInput === 'to-time-picker'}
					>
						<TimePicker
							selected={this.props.selectedToTime}
							onSelect={this.onSelectTime}
							areDatesEqual={areDatesEqual}
						/>
					</FadeTransition>
				</div>
			</div>
		);
	}
}

const mapStateToProps = (state: any) => {
	return {
		selectedFromDate: state.car.selectedFromDate,
		selectedToDate: state.car.selectedToDate,
		selectedFromTime: state.car.selectedFromTime,
		selectedToTime: state.car.selectedToTime,
		openedInput: state.base.openedInput,
		openedChildInput: state.base.openedChildInput,
		autoJump: state.base.autoJump,
	};
};

export default connect(mapStateToProps, {
	selectFromDate,
	selectToDate,
	selectToTime,
	openInput,
	openChildInput,
})(DatePickerTo);
